import { z } from 'zod';
import {
  paginatedResponse,
  paginationQuery,
  nonNegativeDecimalString as decimalString,
} from './common';

/**
 * Schemas for the dedicated `Hardware` catalog table — Romanian "feronerie"
 * (mechanical fittings: hinges, slides, locks, handles, screws, …).
 *
 * `pricePerPiece` is RON canonical. The `time*Sec` fields capture how many
 * seconds each operation (circular-saw cut, CNC, edge-banding, assembly)
 * takes per piece — used by the labor calculator. Source data:
 * `excels/products.xlsx` sheet `Products`.
 *
 * Names are prefixed with `catalog` to avoid collision with the existing
 * project-line `HardwareItem` schemas in `project-hardware.ts`.
 */

export const catalogHardwareCurrencyEnum = z.enum(['RON', 'EUR', 'USD']);
export type CatalogHardwareCurrencyValue = z.infer<typeof catalogHardwareCurrencyEnum>;

const optionalTrim = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v === '' ? undefined : v));

const nonNegativeInt = z.number().int().nonnegative().max(86400);

export const catalogHardwareListItem = z.object({
  id: z.string(),
  name: z.string(),
  offerName: z.string().nullable(),
  sku: z.string().nullable(),
  internalCode: z.string().nullable(),
  manufacturer: z.string().nullable(),
  pricePerPiece: decimalString.optional(),
  currency: catalogHardwareCurrencyEnum,
  /**
   * Default price multiplier for this fitting, used every time it is picked.
   *
   * A DEFAULT, not the truth: each use snapshots it onto
   * `HardwareItem.coefficient`, so changing it here never re-prices work
   * already quoted. Unlike `pricePerPiece` it is not cost-gated — a designer
   * without CATALOG_READ_COST still types markups all day.
   */
  coefficient: decimalString,
  timeCircularSec: z.number().int().nonnegative(),
  timeCncSec: z.number().int().nonnegative(),
  timeCantSec: z.number().int().nonnegative(),
  timeAssemblySec: z.number().int().nonnegative(),
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
});
export type CatalogHardwareListItem = z.infer<typeof catalogHardwareListItem>;

export const catalogHardwareDetails = catalogHardwareListItem.extend({
  attributes: z.unknown().nullable(),
});
export type CatalogHardwareDetails = z.infer<typeof catalogHardwareDetails>;

export const listCatalogHardwareQuery = paginationQuery.extend({
  search: optionalTrim(120),
  manufacturer: optionalTrim(120),
  active: z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
    .optional(),
  includeDeleted: z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
    .optional()
    .default(false),
});
export type ListCatalogHardwareQuery = z.infer<typeof listCatalogHardwareQuery>;

export const listCatalogHardwareResponse = paginatedResponse(catalogHardwareListItem);
export type ListCatalogHardwareResponse = z.infer<typeof listCatalogHardwareResponse>;

export const createCatalogHardwareRequest = z.object({
  name: z.string().trim().min(1).max(200),
  offerName: optionalTrim(200),
  sku: optionalTrim(80),
  internalCode: optionalTrim(120),
  manufacturer: optionalTrim(120),
  pricePerPiece: decimalString,
  currency: catalogHardwareCurrencyEnum.default('RON'),
  coefficient: decimalString.default('2.5'),
  timeCircularSec: nonNegativeInt.default(0),
  timeCncSec: nonNegativeInt.default(0),
  timeCantSec: nonNegativeInt.default(0),
  timeAssemblySec: nonNegativeInt.default(0),
  active: z.boolean().default(true),
  attributes: z.unknown().optional(),
});
export type CreateCatalogHardwareRequest = z.infer<typeof createCatalogHardwareRequest>;

export const updateCatalogHardwareRequest = createCatalogHardwareRequest.partial();
export type UpdateCatalogHardwareRequest = z.infer<typeof updateCatalogHardwareRequest>;
